import React, { useEffect, useRef, useState } from 'react';
import { Button, Drawer, Form, Input, Select, Tag, message } from 'antd';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { fetchPostTags } from '@/apis/posts';
import { getRequestErrorMessage } from '@/utils/requestError';
import { compactQueryParams } from '@/utils/queryParams';
import { useBrowseState } from '@/utils/browseState';
import './index.scss';

const postTypeOptions = [{ value: 'general', label: '普通帖子' }, { value: 'skill_exchange', label: '技能交换' }];
const recruitingOptions = [{ value: 'true', label: '开放招募' }, { value: 'false', label: '暂不招募' }];
const orderingOptions = [{ value: '-created_at', label: '最新发布' }, { value: 'created_at', label: '最早发布' }, { value: '-view_count', label: '浏览量最多' }, { value: 'view_count', label: '浏览量最少' }];

const PostFilters = ({ onChange, disabled, recommended = false, nearby = false, isMobile = false, navigation, extraActions }) => {
  const [form] = Form.useForm();
  const [tags, setTags] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const selectedFilters = Form.useWatch(compactQueryParams, { form, preserve: true }) || {};
  const [savedFilters, setSavedFilters] = useBrowseState(`post-filters:${recommended ? 'recommended' : nearby ? 'nearby' : 'latest'}`, {});
  const appliedFilters = useRef(savedFilters);
  const initialFilters = useRef(savedFilters).current;
  const version = useRef(0);
  useEffect(() => {
    if (!open) return;
    const current = ++version.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await fetchPostTags({ paginate: true, q: query.trim().slice(0, 30), page: 1, pageSize: 50 });
        if (current === version.current) setTags(result.data || []);
      } catch (error) { if (current === version.current) message.error(getRequestErrorMessage(error, '标签加载失败')); }
      finally { if (current === version.current) setLoading(false); }
    }, 300);
    return () => { clearTimeout(timer); version.current += 1; };
  }, [query, open]);
  const submit = values => {
    appliedFilters.current = compactQueryParams(values);
    setSavedFilters(appliedFilters.current);
    onChange(appliedFilters.current);
  };
  const reset = () => {
    form.resetFields();
    form.setFieldsValue({ tag: undefined, post_type: undefined, algorithm: undefined, is_recruiting: undefined, ordering: undefined, keyword: undefined });
    submit({});
  };
  const filterFields = <>
      <Form.Item name="tag" label="标签"><Select allowClear showSearch filterOption={false} placeholder="搜索兴趣标签" loading={loading} onSearch={setQuery} onOpenChange={setOpen} options={tags.map(tag => ({ value: tag.id, label: tag.name }))} /></Form.Item>
      <Form.Item name="post_type" label="类型"><Select allowClear placeholder="全部类型" options={postTypeOptions} /></Form.Item>
      {recommended && <Form.Item name="algorithm" label="推荐方式"><Select allowClear placeholder="兴趣标签推荐" options={[{ value: 'tags', label: '兴趣标签推荐' }, { value: 'hybrid', label: '综合推荐' }]} /></Form.Item>}
      {!recommended && !nearby && <>
        <Form.Item name="is_recruiting" label="招募"><Select allowClear placeholder="全部帖子" options={recruitingOptions} /></Form.Item>
        <Form.Item name="ordering" label="排序"><Select placeholder="最新发布" options={orderingOptions} /></Form.Item>
      </>}
  </>;
  const keywordField = <Form.Item name="keyword" label={isMobile ? undefined : '关键词'}>
    <Input aria-label="关键词" maxLength={200} allowClear placeholder="搜索标题、正文或技能" />
  </Form.Item>;
  if (isMobile) {
    const trigger = <Button className="postFilters-trigger" icon={<FilterOutlined />} aria-label="打开帖子筛选" disabled={disabled} onClick={() => {
      form.setFieldsValue({ tag: undefined, post_type: undefined, algorithm: undefined, is_recruiting: undefined, ordering: undefined, ...appliedFilters.current, keyword: form.getFieldValue('keyword') });
      setDrawerOpen(true);
    }} />;
    return <div className={`postFilters postFilters-mobile${navigation ? '' : ' postFilters-mobile-compact'}`}>
      <Form form={form} initialValues={initialFilters} layout="vertical" disabled={disabled} onFinish={values => submit({ ...appliedFilters.current, keyword: values.keyword })}>
        <div className="postFilters-search" role="search" aria-label="搜索帖子">
          {keywordField}
          <Button type="primary" htmlType="submit" icon={<SearchOutlined />} aria-label="搜索" />
          {extraActions}
          {trigger}
        </div>
        {navigation && <div className="postFilters-navigation">
          {navigation}
        </div>}
        <Drawer title="筛选帖子" placement="right" size="min(320px, calc(100vw - 24px))" className="postFilters-drawer" forceRender open={drawerOpen} onClose={() => setDrawerOpen(false)} footer={<div className="postFilters-actions">
          <Button disabled={disabled} onClick={reset}>重置</Button>
          <Button type="primary" disabled={disabled} onClick={() => { submit(form.getFieldsValue(true)); setDrawerOpen(false); }}>筛选</Button>
        </div>}>
          {filterFields}
        </Drawer>
      </Form>
    </div>;
  }
  const filterLabels = {
    algorithm: value => `推荐：${value === 'hybrid' ? '综合推荐' : '兴趣标签推荐'}`,
    keyword: value => `关键词：${value}`,
    tag: value => `标签：${tags.find(tag => String(tag.id) === String(value))?.name || value}`,
    post_type: value => `类型：${postTypeOptions.find(option => option.value === value)?.label || value}`,
    is_recruiting: value => `招募：${recruitingOptions.find(option => option.value === value)?.label || value}`,
    ordering: value => `排序：${orderingOptions.find(option => option.value === value)?.label || value}`,
  };
  return <><div className="postFilters postFilters-desktop">
    <Form form={form} initialValues={initialFilters} layout="vertical" disabled={disabled} onFinish={submit}>
      <div className="postFilters-toolbar">
        <div className="postFilters-conditions">{filterFields}</div>
        <Form.Item name="keyword" label="关键词" className="postFilters-keyword">
          <Input.Search aria-label="关键词" maxLength={200} allowClear placeholder="搜索标题、正文或技能" disabled={disabled}
            enterButton={<Button type="primary" htmlType="button" disabled={disabled} icon={<SearchOutlined />}>搜索</Button>}
            onSearch={(value, event, info) => {
              if (info?.source === 'clear') return;
              event?.preventDefault();
              if (!disabled) submit({ ...form.getFieldsValue(true), keyword: value });
            }} />
        </Form.Item>
      </div>
      <div className="postFilters-summary">
        <div className="postFilters-selected" aria-label="已选择的筛选条件">
          {Object.keys(selectedFilters).length ? <>
            <span className="postFilters-selected-label">已选择</span>
            {Object.entries(selectedFilters).map(([key, value]) => <Tag key={key} closable={!disabled} onClose={() => {
              form.setFieldsValue({ [key]: undefined });
              if (key in appliedFilters.current) submit({ ...appliedFilters.current, [key]: undefined });
            }}>{filterLabels[key](value)}</Tag>)}
          </> : <span className="postFilters-empty">暂无筛选条件</span>}
        </div>
        <div className="postFilters-desktop-actions">
          {extraActions}
          <Button type="text" onClick={reset}>清空筛选</Button>
          <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>筛选</Button>
        </div>
      </div>
    </Form>
  </div>{navigation}</>;
};
export default PostFilters;
